import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'iqes_access_token';

// Configure axios defaults
axios.defaults.withCredentials = true;

// Restore token from localStorage on app boot (fallback for incognito/3rd-party cookie blocking)
const savedToken = localStorage.getItem(TOKEN_KEY);
if (savedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

// Patch window.fetch globally to auto-inject Authorization header when calling our backend.
// This is critical for incognito mode where cookies are blocked cross-origin.
if (typeof window !== 'undefined' && !window.__fetch_patched__) {
  window.__fetch_patched__ = true;
  const origFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      const url = typeof input === 'string' ? input : input.url;
      // Only inject for same-API origin requests
      if (url && (url.includes(API_URL) || url.startsWith('/api/'))) {
        init = { ...init };
        init.headers = { ...(init.headers || {}), Authorization: `Bearer ${token}` };
        if (init.credentials === undefined) init.credentials = 'include';
      }
    }
    return origFetch(input, init);
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      // Clear stale token if /me fails
      localStorage.removeItem(TOKEN_KEY);
      delete axios.defaults.headers.common['Authorization'];
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    // Store token as fallback for cookie-blocked browsers (incognito)
    if (response.data.access_token) {
      localStorage.setItem(TOKEN_KEY, response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
    }
    setUser(response.data);
    return response.data;
  };

  const clientLogin = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/client-login`,
      { email, password },
      { withCredentials: true }
    );
    if (response.data.access_token) {
      localStorage.setItem(TOKEN_KEY, response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
    }
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common['Authorization'];
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, clientLogin, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

// Helper for fetch() calls to add the Bearer token (for pages using fetch instead of axios)
export function authHeaders() {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
